import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Panel, PanelHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Camera, ChevronLeft, ChevronRight, Building2, AlertTriangle, ImageIcon, Flag, Bell, X } from "lucide-react";
import { toast } from "sonner";

const CAT_LABEL = {
  CLEANING_AM: "Cleaning · AM Shift",
  CLEANING_PM: "Cleaning · PM Shift",
  MAINTENANCE: "Maintenance",
  INVENTORY: "Inventory",
};
const CAT_COLOR = {
  CLEANING_AM: "#7DD3FC",
  CLEANING_PM: "#A78BFA",
  MAINTENANCE: "#FBBF24",
  INVENTORY: "#CCFF00",
};

const isoToday = () => new Date().toISOString().slice(0, 10);
const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

/* ---------------------------------------------------------------- */
/*  BRANCH ADMIN: today's 4 checklists + history + photo requests    */
/* ---------------------------------------------------------------- */
function BranchAdminOps() {
  const { activeBranchId, user } = useAuth();
  const qc = useQueryClient();
  const branchId = activeBranchId || (user?.branch_ids || [])[0];
  const [date, setDate] = useState(isoToday());
  const [notesDraft, setNotesDraft] = useState("");

  const { data: items = [] } = useQuery({
    queryKey: ["ops-items"],
    queryFn: async () => (await apiClient.get("/ops/items")).data,
  });
  const { data: entry } = useQuery({
    enabled: !!branchId,
    queryKey: ["ops-daily", branchId, date],
    queryFn: async () => (await apiClient.get("/ops/daily", { params: { branch_id: branchId, date } })).data,
  });
  const { data: history = [] } = useQuery({
    enabled: !!branchId,
    queryKey: ["ops-history", branchId],
    queryFn: async () => (await apiClient.get("/ops/daily/range", { params: { branch_id: branchId, days: 14 } })).data,
  });
  const { data: photoReqs = [] } = useQuery({
    enabled: !!branchId,
    queryKey: ["ops-photo-reqs", branchId, "PENDING"],
    queryFn: async () => (await apiClient.get("/ops/photo-requests", { params: { branch_id: branchId, status: "PENDING" } })).data,
  });

  React.useEffect(() => { setNotesDraft(entry?.notes || ""); }, [entry?.id, entry?.notes]);

  const tick = useMutation({
    mutationFn: async ({ item_id, done }) =>
      (await apiClient.post("/ops/daily/tick", { branch_id: branchId, date, item_id, done })).data,
    onSuccess: (d) => qc.setQueryData(["ops-daily", branchId, date], d),
  });

  const saveNotes = useMutation({
    mutationFn: async () => (await apiClient.patch(`/ops/daily/${branchId}/${date}/notes`, { notes: notesDraft })).data,
    onSuccess: () => { toast.success("Notes saved"); qc.invalidateQueries({ queryKey: ["ops-daily", branchId, date] }); },
  });

  const uploadPhoto = async (item_id, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("branch_id", branchId);
    fd.append("date", date);
    fd.append("item_id", item_id);
    fd.append("file", file);
    try {
      const { data } = await apiClient.post("/ops/daily/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      qc.invalidateQueries({ queryKey: ["ops-daily", branchId, date] });
      qc.invalidateQueries({ queryKey: ["ops-photo-reqs", branchId] });
      toast.success(data.mocked ? "Photo uploaded (MOCKED)" : "Photo uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  const byCategory = useMemo(() => {
    const m = { CLEANING_AM: [], CLEANING_PM: [], MAINTENANCE: [], INVENTORY: [] };
    for (const it of items) (m[it.category] ??= []).push(it);
    return m;
  }, [items]);

  const status = entry?.items_status || {};
  const photos = entry?.photos || {};
  const totalActive = items.filter((i) => i.active !== false).length;
  const doneCount = items.filter((i) => status[i.id]).length;
  const dayPct = totalActive ? Math.round((doneCount / totalActive) * 100) : 0;
  const disputed = !!entry?.disputed;

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="branch-admin-ops">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Operations · Daily Compliance</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Today&apos;s Checklist</h1>
          <div className="text-zinc-500 text-sm mt-1">4 checklists · cleaning runs twice · photos optional</div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => {
            const d = new Date(date); d.setDate(d.getDate() - 1); setDate(d.toISOString().slice(0, 10));
          }} data-testid="ops-prev"><ChevronLeft size={16} /></Button>
          <div className="bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2">
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
              className="bg-transparent text-white text-sm outline-none font-mono" data-testid="ops-date-input" />
          </div>
          <Button variant="ghost" onClick={() => {
            const d = new Date(date); d.setDate(d.getDate() + 1); setDate(d.toISOString().slice(0, 10));
          }} data-testid="ops-next"><ChevronRight size={16} /></Button>
          <Button variant="outline" onClick={() => setDate(isoToday())} data-testid="ops-today" className="rounded-sm">Today</Button>
        </div>
      </div>

      {/* Score summary */}
      <Panel>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Compliance · {fmtDate(date)}</div>
            <div className="flex items-baseline gap-3 mt-1">
              <div className="font-display text-6xl font-black" style={{ color: dayPct >= 80 ? "#00E676" : dayPct >= 60 ? "#CCFF00" : "#FF3B30" }} data-testid="today-score">
                {dayPct}%
              </div>
              <div className="text-sm text-zinc-500">{doneCount} of {totalActive} items</div>
            </div>
          </div>
          {disputed && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-sm px-3 py-2 text-xs flex items-center gap-2" data-testid="dispute-banner">
              <Flag size={14} />
              <div>
                <div className="uppercase tracking-[0.16em] font-bold">Disputed by Super Admin</div>
                {entry?.dispute_note && <div className="mt-0.5 opacity-80">{entry.dispute_note}</div>}
              </div>
            </div>
          )}
        </div>
      </Panel>

      {/* Photo Requests Inbox */}
      {photoReqs.length > 0 && (
        <Panel>
          <PanelHeader title={`🔔 ${photoReqs.length} Photo Request${photoReqs.length > 1 ? "s" : ""} from Super Admin`}
            subtitle="Upload proof photos on the matching day/item to fulfil these" />
          <div className="space-y-2" data-testid="photo-request-inbox">
            {photoReqs.map((r) => (
              <div key={r.id} data-testid={`photo-req-${r.id}`} className="bg-zinc-950 border border-red-500/30 rounded-sm p-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-red-400 font-bold flex items-center gap-1">
                      <Bell size={11} /> {fmtDate(r.date)} · {r.category ? CAT_LABEL[r.category] : "Any category"}
                    </div>
                    <div className="text-sm mt-1">{r.reason || "(no reason given)"}</div>
                  </div>
                  <Button size="sm" variant="outline" onClick={() => setDate(r.date)} className="rounded-sm text-[10px] uppercase tracking-[0.16em]">
                    Go to that day
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {/* 4 category cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {["CLEANING_AM", "CLEANING_PM", "MAINTENANCE", "INVENTORY"].map((cat) => (
          <CategoryChecklist
            key={cat}
            category={cat}
            items={byCategory[cat] || []}
            status={status}
            photos={photos}
            onTick={(id, done) => tick.mutate({ item_id: id, done })}
            onUpload={uploadPhoto}
          />
        ))}
      </div>

      {/* Notes */}
      <Panel>
        <PanelHeader title="Notes for the day" subtitle="Optional · visible to Super Admin" />
        <Textarea value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} rows={3}
          data-testid="ops-notes" placeholder="Anything to flag — broken equipment, restock urgency, staff issues…"
          className="bg-zinc-950 border-zinc-800 rounded-sm" />
        <Button onClick={() => saveNotes.mutate()} className="mt-3 bg-volt hover:bg-volt-hover text-black font-bold uppercase tracking-wider rounded-sm h-9"
          data-testid="save-notes">Save Notes</Button>
      </Panel>

      {/* 14-day history strip */}
      <Panel>
        <PanelHeader title="Last 14 Days" subtitle="Your compliance trail" />
        <div className="grid grid-cols-7 gap-2" data-testid="history-strip">
          {[...Array(14)].map((_, i) => {
            const d = new Date(); d.setDate(d.getDate() - (13 - i));
            const iso = d.toISOString().slice(0, 10);
            const rec = history.find((r) => r.date === iso);
            const pct = rec ? Math.round((Object.values(rec.items_status || {}).filter(Boolean).length / totalActive) * 100) : 0;
            const color = pct >= 80 ? "#00E676" : pct >= 60 ? "#CCFF00" : pct === 0 ? "#3F3F46" : "#FF3B30";
            return (
              <button key={iso} onClick={() => setDate(iso)} data-testid={`hist-${iso}`}
                className="text-left group bg-zinc-950 border border-zinc-900 hover:border-volt/40 rounded-sm p-2 transition-colors">
                <div className="text-[9px] uppercase tracking-[0.16em] text-zinc-500">{d.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                <div className="font-display text-lg font-black mt-1" style={{ color }}>{rec ? `${pct}%` : "—"}</div>
                {rec?.disputed && <div className="text-[9px] text-red-400 mt-1">disputed</div>}
              </button>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

function CategoryChecklist({ category, items, status, photos, onTick, onUpload }) {
  const color = CAT_COLOR[category];
  const activeItems = items.filter((i) => i.active !== false);
  const done = activeItems.filter((i) => status[i.id]).length;
  const pct = activeItems.length ? Math.round((done / activeItems.length) * 100) : 0;
  return (
    <Panel>
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color }}>{CAT_LABEL[category]}</div>
          <div className="text-xs text-zinc-500">{done} of {activeItems.length} · {pct}%</div>
        </div>
        <div className="h-1.5 w-24 bg-zinc-900 rounded-sm overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
        </div>
      </div>
      <div className="mt-3 space-y-1.5" data-testid={`cat-${category}`}>
        {activeItems.length === 0 && (
          <div className="text-[11px] text-zinc-600 italic">No items in this category — Super Admin can add them.</div>
        )}
        {activeItems.map((it) => {
          const checked = !!status[it.id];
          const itemPhotos = photos[it.id] || [];
          return (
            <div key={it.id} data-testid={`item-${it.id}`} className="flex items-start gap-2 bg-zinc-950 border border-zinc-900 rounded-sm p-2">
              <Checkbox checked={checked} onCheckedChange={(v) => onTick(it.id, !!v)}
                data-testid={`tick-${it.id}`}
                className="mt-0.5 data-[state=checked]:bg-volt data-[state=checked]:text-black border-zinc-700" />
              <div className="flex-1 min-w-0">
                <div className={`text-sm ${checked ? "text-zinc-400 line-through" : "text-zinc-100"}`}>{it.name}</div>
                {itemPhotos.length > 0 && (
                  <div className="flex gap-1 mt-1.5 flex-wrap">
                    {itemPhotos.slice(0, 4).map((u) => {
                      const url = u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`;
                      return (
                        <a key={u} href={url} target="_blank" rel="noreferrer"
                          className="block w-8 h-8 rounded-sm overflow-hidden border border-zinc-800 hover:border-volt">
                          <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                        </a>
                      );
                    })}
                    {itemPhotos.length > 4 && <div className="text-[10px] text-zinc-500 self-center">+{itemPhotos.length - 4}</div>}
                  </div>
                )}
              </div>
              <label className="cursor-pointer text-zinc-500 hover:text-volt shrink-0 p-1" title="Add photo (optional)">
                <Camera size={14} />
                <input type="file" accept="image/*" className="hidden"
                  data-testid={`photo-input-${it.id}`}
                  onChange={(e) => onUpload(it.id, e.target.files?.[0])} />
              </label>
            </div>
          );
        })}
      </div>
    </Panel>
  );
}


/* ---------------------------------------------------------------- */
/*  SUPER ADMIN: branch scorecard + drill-down + photo request       */
/* ---------------------------------------------------------------- */
function SuperAdminOps() {
  const qc = useQueryClient();
  const [drillBranch, setDrillBranch] = useState(null);
  const [days, setDays] = useState(7);

  const { data: overview } = useQuery({
    queryKey: ["ops-score-all", days],
    queryFn: async () => (await apiClient.get("/ops/score/all", { params: { days } })).data,
  });

  return (
    <div className="p-8 space-y-6 animate-fade-in-up" data-testid="super-admin-ops">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-volt font-semibold">Operations · Maintenance Score</div>
          <h1 className="font-display text-5xl font-black uppercase tracking-tight mt-1">Branch Compliance</h1>
          <div className="text-zinc-500 text-sm mt-1">Rolling score of daily checklists · click a branch to drill into any day</div>
        </div>
        <div className="flex gap-2">
          {[7, 14, 30].map((n) => (
            <Button key={n} variant={days === n ? "default" : "outline"} onClick={() => setDays(n)}
              data-testid={`days-${n}`}
              className={days === n ? "bg-volt text-black rounded-sm" : "bg-zinc-950 border-zinc-800 rounded-sm"}>
              {n}-day
            </Button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="branch-scorecards">
        {(overview?.branches || []).map((b) => {
          const pct = b.rolling_score_pct;
          const color = pct >= 80 ? "#00E676" : pct >= 60 ? "#CCFF00" : "#FF3B30";
          return (
            <button key={b.branch_id} onClick={() => setDrillBranch(b)}
              data-testid={`scorecard-${b.branch_id}`}
              className="text-left group focus:outline-none focus:ring-2 focus:ring-volt/50 rounded-sm">
              <Panel className="hover:border-volt/40 transition-colors cursor-pointer">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-bold">
                      <Building2 size={12} /> {b.branch_name}
                    </div>
                    <div className="font-display text-6xl font-black mt-2" style={{ color }}>{pct}%</div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {b.missed_days} missed · {b.disputed_days > 0 ? <span className="text-red-400">{b.disputed_days} disputed</span> : "0 disputed"}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mt-3 group-hover:text-volt transition-colors">Click to inspect →</div>
                  </div>
                  {b.disputed_days > 0 && <AlertTriangle size={20} className="text-red-400" />}
                </div>
              </Panel>
            </button>
          );
        })}
      </div>

      <SuperAdminBranchDrill branch={drillBranch} onClose={() => setDrillBranch(null)}
        onAction={() => { qc.invalidateQueries({ queryKey: ["ops-score-all"] }); }} />
    </div>
  );
}

function SuperAdminBranchDrill({ branch, onClose, onAction }) {
  const [selectedDate, setSelectedDate] = useState(null);
  const { data: score } = useQuery({
    enabled: !!branch,
    queryKey: ["ops-score-drill", branch?.branch_id],
    queryFn: async () => (await apiClient.get("/ops/score", { params: { branch_id: branch.branch_id, days: 30 } })).data,
  });

  React.useEffect(() => { if (!branch) setSelectedDate(null); }, [branch]);

  return (
    <>
      <Sheet open={!!branch} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent side="right" className="w-full sm:max-w-3xl bg-zinc-950 border-zinc-800 overflow-y-auto scrollbar-thin" data-testid="branch-drill">
          <SheetHeader>
            <SheetTitle className="font-display uppercase tracking-tight text-2xl">{branch?.branch_name}</SheetTitle>
            <SheetDescription className="text-zinc-500 text-xs uppercase tracking-[0.16em]">
              30-day trail · click any day for details & to request photos
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4 grid grid-cols-6 sm:grid-cols-10 gap-1.5" data-testid="drill-days">
            {(score?.daily || []).map((d) => {
              const color = d.score_pct >= 80 ? "#00E676" : d.score_pct >= 60 ? "#CCFF00" : d.score_pct === 0 ? "#3F3F46" : "#FF3B30";
              return (
                <button key={d.date} onClick={() => setSelectedDate(d.date)}
                  data-testid={`drill-day-${d.date}`}
                  className="bg-black border border-zinc-900 hover:border-volt/50 rounded-sm p-2 text-left transition-colors">
                  <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-500">{new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}</div>
                  <div className="font-display text-base font-black mt-1" style={{ color }}>{d.has_entry ? `${d.score_pct}%` : "—"}</div>
                  {d.disputed && <div className="text-[9px] text-red-400 mt-0.5">flagged</div>}
                </button>
              );
            })}
          </div>
        </SheetContent>
      </Sheet>

      <DayDetailDialog branchId={branch?.branch_id} date={selectedDate} onClose={() => setSelectedDate(null)} onAction={onAction} />
    </>
  );
}

function DayDetailDialog({ branchId, date, onClose, onAction }) {
  const qc = useQueryClient();
  const [reason, setReason] = useState("");
  const [disputeNote, setDisputeNote] = useState("");
  const { data: items = [] } = useQuery({
    queryKey: ["ops-items"],
    queryFn: async () => (await apiClient.get("/ops/items")).data,
  });
  const { data: entry } = useQuery({
    enabled: !!branchId && !!date,
    queryKey: ["ops-day-detail", branchId, date],
    queryFn: async () => (await apiClient.get("/ops/daily", { params: { branch_id: branchId, date } })).data,
  });

  React.useEffect(() => {
    setReason("");
    setDisputeNote(entry?.dispute_note || "");
  }, [entry?.id, date]);

  const requestPhotos = useMutation({
    mutationFn: async (category) => (await apiClient.post("/ops/photo-requests", {
      branch_id: branchId, date, category, reason,
    })).data,
    onSuccess: () => { toast.success("Photo request sent to Branch Admin"); qc.invalidateQueries({ queryKey: ["ops-photo-reqs"] }); onAction?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const dispute = useMutation({
    mutationFn: async (flag) => (await apiClient.patch(`/ops/daily/${branchId}/${date}/dispute`, {
      disputed: flag, dispute_note: disputeNote,
    })).data,
    onSuccess: () => { toast.success("Dispute status updated"); qc.invalidateQueries({ queryKey: ["ops-day-detail", branchId, date] }); onAction?.(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  if (!date) return null;
  const status = entry?.items_status || {};
  const photos = entry?.photos || {};

  const byCategory = { CLEANING_AM: [], CLEANING_PM: [], MAINTENANCE: [], INVENTORY: [] };
  for (const it of items) (byCategory[it.category] ??= []).push(it);

  return (
    <Dialog open={!!date} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-zinc-950 border-zinc-800 rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="day-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">{fmtDate(date)} — detail</DialogTitle>
          <DialogDescription className="text-zinc-500 text-xs">
            Read-only view · request photos or flag the day as disputed
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {["CLEANING_AM", "CLEANING_PM", "MAINTENANCE", "INVENTORY"].map((cat) => {
            const catItems = byCategory[cat] || [];
            const done = catItems.filter((i) => status[i.id]).length;
            return (
              <div key={cat} className="bg-black border border-zinc-900 rounded-sm p-3" data-testid={`day-cat-${cat}`}>
                <div className="flex items-center justify-between">
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold" style={{ color: CAT_COLOR[cat] }}>{CAT_LABEL[cat]}</div>
                  <div className="text-xs text-zinc-500">{done}/{catItems.length}</div>
                </div>
                <div className="mt-2 space-y-1">
                  {catItems.map((it) => (
                    <div key={it.id} className="flex items-center gap-2 text-xs">
                      <div className={`w-2 h-2 rounded-full ${status[it.id] ? "bg-volt" : "bg-zinc-700"}`} />
                      <div className={`flex-1 min-w-0 truncate ${status[it.id] ? "text-zinc-400" : "text-zinc-500"}`}>{it.name}</div>
                      {(photos[it.id] || []).length > 0 && (
                        <ImageIcon size={12} className="text-volt shrink-0" />
                      )}
                    </div>
                  ))}
                </div>
                <Button size="sm" variant="outline" onClick={() => requestPhotos.mutate(cat)}
                  data-testid={`request-photo-${cat}`}
                  disabled={requestPhotos.isPending}
                  className="mt-3 w-full h-8 bg-zinc-900 border-zinc-800 text-[10px] uppercase tracking-[0.16em] rounded-sm">
                  <Camera size={11} className="mr-1" /> Request Photo
                </Button>
              </div>
            );
          })}
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Reason for photo request (optional)</div>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Verify morning cleaning was done"
            data-testid="photo-reason" className="mt-1 bg-black border-zinc-800 rounded-sm" />
        </div>

        {entry?.notes && (
          <div className="bg-black border border-zinc-900 rounded-sm p-3">
            <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500 font-semibold">Branch Admin Notes</div>
            <div className="text-sm text-zinc-300 mt-1">{entry.notes}</div>
          </div>
        )}

        <div className="bg-red-500/5 border border-red-500/20 rounded-sm p-3 space-y-2">
          <div className="text-[10px] uppercase tracking-[0.22em] text-red-400 font-bold flex items-center gap-1">
            <Flag size={11} /> Dispute this day
          </div>
          <Textarea rows={2} value={disputeNote} onChange={(e) => setDisputeNote(e.target.value)} data-testid="dispute-note"
            placeholder="Reason for dispute (e.g., score looks fudged, no photos to back it)" className="bg-black border-zinc-800 rounded-sm text-sm" />
          <div className="flex gap-2">
            {entry?.disputed ? (
              <Button size="sm" variant="outline" onClick={() => dispute.mutate(false)} data-testid="clear-dispute"
                className="rounded-sm text-[10px] uppercase tracking-[0.16em]"><X size={11} className="mr-1" /> Clear dispute</Button>
            ) : (
              <Button size="sm" onClick={() => dispute.mutate(true)} data-testid="flag-dispute"
                disabled={dispute.isPending}
                className="bg-red-500 hover:bg-red-600 text-white rounded-sm text-[10px] uppercase tracking-[0.16em]">
                <Flag size={11} className="mr-1" /> Flag as disputed
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ---------------------------------------------------------------- */
/*  ROUTER                                                            */
/* ---------------------------------------------------------------- */
export default function OperationsPage() {
  const { user } = useAuth();
  if (user?.role === "SUPER_ADMIN") return <SuperAdminOps />;
  if (user?.role === "ADMIN") return <BranchAdminOps />;
  return <div className="p-8 text-zinc-500">Operations is for Super Admin and Branch Admins.</div>;
}
